import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, RotateCw } from "lucide-react";
import { useLang } from "@/lib/i18n";

const HERO_DEFAULT = {
  heading: ["Rätt kompetens.", "Rätt plats.", "Hållbar rotation.", "Samlad kommunikation."],
  ingress: "Kompetensbaserad bemanning, transparent belastningsuppföljning och godkännandestyrda flöden – med ett gemensamt flöde som håller hela teamet informerat. För industri, lager och e-handel.",
  features: [
    "Transparenta, förklarbara beräkningar",
    "Internt flöde, enkäter & utskick på ett ställe",
    "Godkännande krävs för alla utskick",
    "Tenant-isolering & auditlogg",
    "Installerbar som app (PWA)",
  ],
  footer: "Ingen fiktiv personaldata. Organisationen börjar tom.",
};

function Frame({ children }) {
  const [hero, setHero] = useState(HERO_DEFAULT);
  useEffect(() => {
    api.get("/content/landing").then((r) => setHero({ ...HERO_DEFAULT, ...r.data })).catch(() => {});
  }, []);
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden flex-col justify-between bg-primary p-12 text-primary-foreground lg:flex">
        <div className="flex items-center gap-3">
          <img src="/brand-logo.png" alt="" className="h-11 w-11 rounded-xl object-contain" />
          <div className="font-head text-lg font-bold leading-tight">Kompetens<br /><span className="text-blue-200 text-sm">& Rotation</span></div>
        </div>
        <div>
          <h1 className="font-head text-4xl font-extrabold leading-tight">
            {hero.heading.map((h, i) => <span key={i}>{h}<br /></span>)}
          </h1>
          <p className="mt-4 max-w-md text-blue-100">{hero.ingress}</p>
          <ul className="mt-8 space-y-2 text-sm text-blue-100">
            {hero.features.map((t) => (
              <li key={t} className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-300" /> {t}</li>
            ))}
          </ul>
        </div>
        <p className="text-xs text-blue-300">{hero.footer}</p>
      </div>
      <div className="flex items-center justify-center bg-background p-6 kr-grid-bg">
        <div className="w-full max-w-sm">{children}</div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", company_name: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await api.post("/auth/register", { email: form.email, password: form.password, name: form.name });
        await login(form.email, form.password);
        toast.success(t("register_toast"));
      }
      nav("/");
    } catch (err) {
      toast.error(apiErr(err, t("login_failed")));
    } finally { setLoading(false); }
  };

  return (
    <Frame>
      <div className="mb-6 lg:hidden flex items-center gap-3">
        <img src="/brand-logo.png" alt="" className="h-10 w-10 rounded-xl object-contain" />
        <span className="font-head text-lg font-bold">Kompetens & Rotation</span>
      </div>
      <h2 className="font-head text-2xl font-bold">{mode === "login" ? t("login_title") : t("register_title")}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{mode === "login" ? t("login_sub") : t("register_sub")}</p>
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
        {mode === "register" && (
          <div><Label htmlFor="name">{t("name")}</Label>
            <Input id="name" value={form.name} onChange={set("name")} required data-testid="name-input" /></div>
        )}
        <div><Label htmlFor="email">{t("email")}</Label>
          <Input id="email" type="email" value={form.email} onChange={set("email")} required data-testid="email-input" /></div>
        <div><Label htmlFor="password">{t("password")}</Label>
          <Input id="password" type="password" value={form.password} onChange={set("password")} required data-testid="password-input" /></div>
        <Button type="submit" className="w-full kr-focus" disabled={loading} data-testid="submit-auth">
          {loading && <RotateCw className="mr-2 h-4 w-4 animate-spin" />}
          {mode === "login" ? t("login_btn") : t("create_account_btn")}
        </Button>
      </form>
      <div className="mt-4 flex items-center justify-between text-sm">
        <button className="text-accent hover:underline" data-testid="toggle-mode"
          onClick={() => setMode((m) => (m === "login" ? "register" : "login"))}>
          {mode === "login" ? t("create_org") : t("have_account")}
        </button>
        {mode === "login" && <Link to="/forgot-password" className="text-muted-foreground hover:underline" data-testid="forgot-link">{t("forgot_pw")}</Link>}
      </div>
    </Frame>
  );
}

export function ForgotPage() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); } catch (err) {}
    setSent(true);
  };
  return (
    <Frame>
      <h2 className="font-head text-2xl font-bold">{t("reset_pw")}</h2>
      {sent ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="forgot-confirm">{t("forgot_confirm")}</p>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div><Label htmlFor="fe">{t("email")}</Label>
            <Input id="fe" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" /></div>
          <Button type="submit" className="w-full" data-testid="forgot-submit">{t("send_link")}</Button>
        </form>
      )}
      <Link to="/login" className="mt-4 block text-sm text-accent hover:underline">{t("back_to_login")}</Link>
    </Frame>
  );
}

export function ResetPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: params.get("token"), password });
      toast.success("Lösenordet har återställts. Logga in.");
      nav("/login");
    } catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <Frame>
      <h2 className="font-head text-2xl font-bold">Nytt lösenord</h2>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div><Label htmlFor="np">Nytt lösenord</Label>
          <Input id="np" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password" /></div>
        <Button type="submit" className="w-full" data-testid="reset-submit">Spara nytt lösenord</Button>
      </form>
      <Link to="/login" className="mt-4 block text-sm text-accent hover:underline">Tillbaka till inloggning</Link>
    </Frame>
  );
}
